import React, { useState } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import useLocalStorage from 'react-use/lib/useLocalStorage';
import { useSelector } from 'react-redux';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { HtmlTable } from '../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { HelperText } from '../../../../../CommonComponents/OPL/HelperText/HelperText';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Toggle } from '../../../../../CommonComponents/OPL/Toggle/Toggle';
import { AgreementDTO } from '../../api/useFirmSettingsQuery';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { MarginProps } from '../../../../../theme/styledProps';
import { downloadFile } from '../../../../../CommonUtils/downloadFile';
import { useDownloadAgreementMutation } from './api/useDownloadAgreementMutation';
import { FullPageLoading } from '../../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { selectCurrentAdviserId } from '../../../../../AdviserArea/AdviserAreaSelector';
import { UploadAgreementsModal } from './components/UploadAgreementsModal/UploadAgreementsModal';
import { AgreementRow } from './components/AgreementRow/AgreementRow';
import { FormValues as MySettingsFormValues } from '../../../MySettings/MySettings.meta';
import { FormValues as FirmFormValues } from '../../FirmSettings.meta';

const headers = ['Name', 'File', 'Active', 'Last updated', 'File size'];

interface Props extends MarginProps {
  isFirm?: boolean;
  formBag: UseFormReturn<any>;
  agreements: AgreementDTO[];
}

export const AutoAccountsAgreements: React.FC<Props> = ({
  isFirm,
  formBag,
  agreements,
  ...rest
}) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const adviserId = useSelector(selectCurrentAdviserId);

  const [hideInactiveAgreements, setHideInactiveAgreements] = useLocalStorage(
    // The prefix "permanent-" is mandatory, otherwise the local
    // storage will be cleared during logout
    `permanent-true-${adviserId}-${isFirm ? 'Firm' : 'My'}-Hide-Inactive-Agreements`,
    true,
  );

  const {
    mutateAsync: downloadAgreement,
    isLoading: isDownloadingAgreement,
  } = useDownloadAgreementMutation();

  const { fields, append } = useFieldArray<MySettingsFormValues | FirmFormValues, 'agreements'>({
    control: formBag.control,
    name: 'agreements',
  });

  const handleDownloadAgreement = async (agreementId: string, name: string) => {
    try {
      const { fileName, fileBlob } = await downloadAgreement({
        isFirm,
        agreementId,
      });

      downloadFile(fileName || name, fileBlob);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddAgreements = (newAgreements: AgreementDTO[]) => {
    newAgreements.forEach(agreement => {
      append({ name: agreement.name, isActive: agreement.isActive });
    });

    setIsUploadModalOpen(false);
  };

  const filteredAgreements = fields.filter((_, index) => {
    // TODO: using formBag.watch here is expensive.
    // We could try to either:
    // - Use a mutation observer to render the table's empty state imperatively
    // - Use CSS to make the empty state be below the table rows
    const watchedAgreement = formBag.watch('agreements')[index];

    if (!watchedAgreement) {
      return false;
    }

    return hideInactiveAgreements ? watchedAgreement.isActive : true;
  });

  return (
    <>
      {isDownloadingAgreement && <FullPageLoading />}

      <Box as="section" {...rest}>
        <SectionHeader
          title="Automated Account Agreements"
          description="Add any agreements or disclosures you want made available for the Automated Account workflow"
        />

        <HtmlTable mt="24">
          <table>
            <colgroup>
              <col width="40%" />
              <col width="30%" />
              <col width="10%" />
              <col width="0%" />
            </colgroup>

            <caption>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <span>Agreements</span>

                <Button
                  leftIcon="fas fa-plus"
                  variant="subtleActive"
                  onClick={() => setIsUploadModalOpen(true)}
                >
                  Add Agreements
                </Button>
              </Box>
            </caption>

            <thead>
              <tr>
                {headers.map(text => (
                  <th key={text}>
                    <Text variant="smallCaps" textAlign="left">
                      {text}
                    </Text>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {fields.length === 0 ? (
                <tr>
                  <td colSpan={headers.length}>
                    <HelperText>You have not added any items.</HelperText>
                  </td>
                </tr>
              ) : (
                <>
                  <>
                    {filteredAgreements.length === 0 && (
                      <tr>
                        <td colSpan={headers.length}>
                          <HelperText>You don&apos;t have any active items.</HelperText>
                        </td>
                      </tr>
                    )}
                  </>

                  <>
                    {fields.map((field, index) => {
                      return (
                        <AgreementRow
                          key={field.id}
                          formBag={formBag}
                          agreement={agreements[index]}
                          defaultValue={{ name: field.name, isActive: field.isActive }}
                          hideInactiveAgreements={hideInactiveAgreements}
                          index={index}
                          onDownloadAgreement={handleDownloadAgreement}
                        />
                      );
                    })}
                  </>
                </>
              )}
            </tbody>
          </table>
        </HtmlTable>

        {fields.length > 0 && (
          <Toggle
            checked={hideInactiveAgreements}
            onChange={evt => setHideInactiveAgreements(evt.target.checked)}
            mt="24"
          >
            Hide Inactive Agreements
          </Toggle>
        )}

        {isFirm && (
          <Toggle {...formBag.register('allowAdvisersOverrideAgreements')} mt="24">
            Allow advisors to override Automated Account Disclosures
          </Toggle>
        )}
      </Box>

      {isUploadModalOpen && (
        <UploadAgreementsModal
          isFirm={isFirm}
          agreements={agreements}
          onAddAgreements={handleAddAgreements}
          onClose={() => setIsUploadModalOpen(false)}
        />
      )}
    </>
  );
};
